import React, { useEffect, useState, useCallback } from "react";
import { Button, message, Avatar, Col, Spin } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useAuth from "../../../hooks/useAuth";
import instance from "../../../api/cmsapi";
import QuillHtmlDisplay from "components/QuillHtmlDisplay";
import { quillFormats, quillModules } from "utils/quillEditorConfig";
import {
  filterCommentsForViewer,
  normalizeAprReportRole,
  shouldShowCommentAuthorToViewer,
} from "utils/aprCommentVisibility";

function resolveStatusString(assessmentStatus) {
  if (!assessmentStatus) return "";
  if (typeof assessmentStatus === "string") return assessmentStatus;
  if (typeof assessmentStatus === "object" && assessmentStatus.status) {
    return String(assessmentStatus.status);
  }
  return "";
}

function APRmemo({
  year,
  districtId,
  tableCommentedId,
  hideTableDis = false,
  assessmentStatus: assessmentStatusProp = null,
  period = "yearly",
}) {
  const { user } = useAuth();
  const [memoContent, setMemoContent] = useState("");
  const [loading, setLoading] = useState(() => Boolean(districtId && year));
  const [comments, setComments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [localAssessmentStatus, setLocalAssessmentStatus] = useState(null);

  const isAprUser = (user?.user?.userRoles || []).some((role) => role.name === "APR USER");
  const isSuperuser = (user?.user?.userRoles || []).some((role) => role.name === "Superuser");
  const canEditByRole = isAprUser || isSuperuser;
  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";
  const storedUserRole = "APR_USER";
  const viewerRole = normalizeAprReportRole(user);

  const propStatus = resolveStatusString(assessmentStatusProp);
  const resolvedStatus =
    propStatus ||
    localAssessmentStatus?.status ||
    "";

  const fetchAssessmentStatus = useCallback(async () => {
    if (!districtId || !year || propStatus) return;

    const assessmentTypes = [
      `MMDA_APR_${period || "yearly"}`,
      "MMDA_APR_yearly",
      "MMDA_APR_Y",
      "APR",
    ];

    for (const assessmentType of assessmentTypes) {
      try {
        const response = await instance.get(
          `assessments/dpat/${districtId}/${year}/${assessmentType}`,
        );
        if (response?.data?.status) {
          setLocalAssessmentStatus(response.data);
          return;
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.error("Failed to fetch APR assessment status:", err);
        }
      }
    }
  }, [districtId, year, period, propStatus]);

  useEffect(() => {
    fetchAssessmentStatus();
  }, [fetchAssessmentStatus]);

  const fetchComments = useCallback(async () => {
    if (!districtId || !year || !tableCommentedId) return;
    setLoading(true);
    try {
      const response = await instance.get(`comments/tables/${districtId}/${year}/APR`);
      const filteredComments = (response.data || []).filter(
        (comment) =>
          comment.tableCommented === tableCommentedId &&
          comment.districtId === districtId &&
          (comment.userRole === "APR_USER" ||
            comment.userRole === "APR_RCC" ||
            comment.userRole === "RCC_USER") &&
          comment.gaps &&
          String(comment.gaps).trim() !== "",
      );
      setComments(filterCommentsForViewer(filteredComments, viewerRole));
      setError(null);
    } catch (err) {
      setError("Failed to fetch table description");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [districtId, year, tableCommentedId, viewerRole]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const getExistingUserMemo = () =>
    comments.find(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        comment.districtId === districtId &&
        comment.userRole === "APR_USER" &&
        comment.username === currentUsername &&
        comment.gaps &&
        String(comment.gaps).trim() !== "",
    );

  const statusLower = String(resolvedStatus || "").toLowerCase();
  const isTerminal = ["closed", "completed"].includes(statusLower);
  // Trust parent hideTableDis only — local assessment fetch can resolve a stale
  // Closed/Completed record and incorrectly hide the description editor.
  const memoEditable = canEditByRole && !hideTableDis;

  const handleSaveMemo = async () => {
    if (!memoContent.trim()) {
      message.error("Description cannot be empty");
      return;
    }
    if (!currentUsername || !districtId || !year) {
      message.error("User or district information is missing");
      return;
    }
    if (!memoEditable) {
      message.error("You do not have permission to save this table description");
      return;
    }

    const existingComment =
      getExistingUserMemo() ||
      comments.find(
        (comment) =>
          comment.tableCommented === tableCommentedId &&
          comment.districtId === districtId &&
          comment.userRole === "APR_USER" &&
          comment.username === currentUsername,
      );

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const payload = {
      id: existingComment?.id || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: storedUserRole,
      type: "APR",
      districtId,
      year,
      tableCommented: tableCommentedId,
      comments: existingComment?.comments || "",
      gaps: memoContent,
      commentDate: existingComment?.commentDate || commentDate,
      updateDate: commentDate,
      dddpDataDate: commentDate,
      dddpData: { indicator: tableCommentedId, tables: "description" },
    };

    setLoading(true);
    try {
      if (existingComment?.id) {
        await instance.put(`comments/${existingComment.id}`, payload);
        message.success("Table description updated successfully");
      } else {
        await instance.post("comments", payload);
        message.success("Table description added successfully");
      }
      setMemoContent("");
      setEditing(false);
      await fetchComments();
    } catch (err) {
      await fetchComments();
      setError("Failed to save description");
      message.error(`Failed to save description: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    const existingComment = getExistingUserMemo() || comments[0];
    if (existingComment) {
      setMemoContent(existingComment.gaps || "");
    }
    setEditing(true);
  };

  const hasSavedMemo = comments.some((comment) => comment.gaps && String(comment.gaps).trim() !== "");

  // Hide empty description blocks for viewers who cannot edit.
  if (!memoEditable && !hasSavedMemo && !loading) {
    return null;
  }

  const hasSavedUserMemo = Boolean(getExistingUserMemo());
  const showMemoEditor = memoEditable && (editing || !hasSavedMemo);

  return (
    <div className="apr-table-description" style={{ padding: "12px 0 20px" }}>
      <div style={{ marginBottom: 8 }}>
        <strong style={{ fontSize: 13, color: "#16325a" }}>Table Description</strong>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
          Provide a detailed description for this table. Visible to all report viewers.
        </div>
      </div>

      {showMemoEditor && (
        <div>
          <Spin spinning={loading}>
            <ReactQuill
              value={memoContent}
              onChange={setMemoContent}
              readOnly={loading}
              modules={quillModules}
              formats={quillFormats}
              style={{ height: "100px", marginBottom: "50px" }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <Button type="primary" onClick={handleSaveMemo} disabled={loading}>
                <span style={{ color: "white", fontSize: "14px", fontWeight: "bold" }}>
                  SAVE TABLE DESCRIPTION
                </span>
              </Button>
              {editing && (
                <Button
                  onClick={() => {
                    setEditing(false);
                    setMemoContent("");
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              )}
            </div>
          </Spin>
          {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
        </div>
      )}

      {hasSavedMemo && !editing && (
        <div
          style={{
            borderTop: showMemoEditor ? "1px solid #e8e8e8" : "none",
            padding: "8px 0",
            background: "#fff",
            width: "100%",
            marginTop: showMemoEditor ? 12 : 0,
          }}
        >
          {comments.map((comment) => {
            const showAuthor = shouldShowCommentAuthorToViewer(comment, viewerRole, {
              assessmentStatus: resolvedStatus,
              hideAuthorsForFinalReport: isTerminal,
            });
            return (
              <div
                key={comment.id}
                style={{
                  padding: "10px",
                  border: "1px solid #f0f0f0",
                  borderRadius: "6px",
                  marginBottom: "10px",
                }}
              >
                <div style={{ display: "flex", marginBottom: "10px" }}>
                  {showAuthor && (
                    <Col className="apr-author-meta">
                      <Avatar
                        src={
                          comment.userImage ||
                          "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
                        }
                        style={{ marginRight: "10px", borderRadius: "50%" }}
                        size={32}
                      />
                    </Col>
                  )}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      {showAuthor && (
                        <h4 className="apr-author-meta" style={{ margin: 0, fontSize: "13px" }}>
                          {comment.fullName} ({String(comment.userRole || "").replace(/_/g, " ")})
                        </h4>
                      )}
                      {memoEditable &&
                        (comment.username === currentUsername || !hasSavedUserMemo) && (
                          <EditOutlined
                            className="no-print"
                            style={{ cursor: "pointer", color: "#000", marginLeft: "10px" }}
                            onClick={handleEdit}
                          />
                        )}
                    </div>
                    <QuillHtmlDisplay html={comment.gaps} style={{ marginTop: "8px" }} />
                    <Col align="end" className="apr-author-meta">
                      <h6 style={{ marginLeft: "8px", marginTop: 8, fontSize: 12, color: "#64748b" }}>
                        {Array.isArray(comment.commentDate)
                          ? comment.commentDate.join("/")
                          : comment.commentDate || ""}
                      </h6>
                    </Col>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default APRmemo;
